'use client';

import React, { useEffect, useMemo, useState } from 'react';
import Icon from '@/components/ui/AppIcon';
import BoardEnquiryButton from '@/components/BoardEnquiryButton';
import { createClient } from '@/lib/supabase/client';
import {
  fetchBoardEntries,
  categoriesOf,
  countiesOf,
  priceLabel,
  whereLabel,
  type BoardEntry,
  type BoardKey,
} from '@/lib/boards';

/**
 * The shared body of all four service boards.
 *
 * The four pages differ in wording and in which fields matter, not in shape, so
 * they share this and pass their own labels in. Categories and counties come from
 * whatever entries actually exist rather than a fixed list, so the filters can
 * never offer something with nothing behind it.
 */
export default function BoardList({
  board,
  emptyTitle,
  emptyMessage,
  enquiryLabel = 'Make an enquiry',
  showCoverage = false,
  hideWhenEmpty = false,
  heading,
  headingBlurb,
}: {
  board: BoardKey;
  emptyTitle: string;
  emptyMessage: string;
  enquiryLabel?: string;
  /** Optional title above the list — only drawn when there are entries. */
  heading?: string;
  headingBlurb?: string;
  /** Hauliers cover areas; a bale of straw sits in one place. */
  showCoverage?: boolean;
  /**
   * Render nothing at all when the board is empty, instead of an empty state.
   * Used where this board is a secondary section under something else — an
   * "also available" heading over a "nothing here" box just looks broken.
   */
  hideWhenEmpty?: boolean;
}) {
  const [entries, setEntries] = useState<BoardEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [category, setCategory] = useState<string>('all');
  const [county, setCounty] = useState<string>('all');
  const [q, setQ] = useState('');

  useEffect(() => {
    let live = true;
    setLoading(true);
    fetchBoardEntries(createClient(), board)
      .then((rows) => { if (live) { setEntries(rows); setError(null); } })
      .catch((err) => { if (live) setError(err?.message ?? 'Could not load this board.'); })
      .finally(() => { if (live) setLoading(false); });
    return () => { live = false; };
  }, [board]);

  const categories = useMemo(() => categoriesOf(entries), [entries]);
  const counties = useMemo(() => countiesOf(entries), [entries]);

  const shown = useMemo(() => {
    const term = q.trim().toLowerCase();
    return entries.filter((e) => {
      if (category !== 'all' && e.category !== category) return false;
      if (county !== 'all' && e.county !== county) return false;
      if (!term) return true;
      return [e.title, e.subtitle, e.description, e.category, e.county, e.coverage]
        .filter(Boolean)
        .some((v) => (v as string).toLowerCase().includes(term));
    });
  }, [entries, category, county, q]);

  if (loading) {
    if (hideWhenEmpty) return null;
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5" aria-hidden="true">
        {[0, 1, 2, 3, 4, 5].map((i) => (
          <div key={i} className="card h-56 animate-pulse bg-muted/40" />
        ))}
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-start gap-3 bg-red-50 border border-red-200 rounded-xl px-5 py-4" role="alert">
        <Icon name="ExclamationTriangleIcon" size={20} className="text-red-600 shrink-0 mt-0.5" />
        <p className="text-sm text-red-800">{error}</p>
      </div>
    );
  }

  if (entries.length === 0) {
    if (hideWhenEmpty) return null;
    return (
      <div className="card text-center py-16">
        <div className="w-14 h-14 rounded-full bg-muted flex items-center justify-center mx-auto mb-4">
          <Icon name="InboxIcon" size={26} className="text-muted-foreground" />
        </div>
        <h3 className="font-bold text-foreground mb-2">{emptyTitle}</h3>
        <p className="text-sm text-muted-foreground max-w-md mx-auto">{emptyMessage}</p>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-6">
      {heading && (
        <div>
          <h2 className="text-lg font-extrabold text-foreground">{heading}</h2>
          {headingBlurb && <p className="text-sm text-muted-foreground mt-1">{headingBlurb}</p>}
        </div>
      )}

      {/* Filters — only offered when there is something to filter */}
      {(categories.length > 1 || counties.length > 1 || entries.length > 4) && (
        <div className="flex flex-col gap-3">
          <div className="relative max-w-md">
            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">
              <Icon name="MagnifyingGlassIcon" size={16} />
            </span>
            <input
              className="form-input pl-9"
              placeholder="Search this board…"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              aria-label="Search this board"
            />
          </div>

          {categories.length > 1 && (
            <div className="flex gap-2 flex-wrap">
              {['all', ...categories].map((c) => (
                <button
                  key={c}
                  onClick={() => setCategory(c)}
                  className={`px-3 py-1.5 rounded-full text-sm font-medium border transition-all ${
                    category === c
                      ? 'bg-primary text-white border-primary'
                      : 'bg-white text-gray-600 border-gray-200 hover:border-primary/40'
                  }`}
                >
                  {c === 'all' ? 'All' : c}
                </button>
              ))}
            </div>
          )}

          {counties.length > 1 && (
            <div className="max-w-xs">
              <select
                className="form-input"
                value={county}
                onChange={(e) => setCounty(e.target.value)}
                aria-label="Filter by county"
              >
                <option value="all">All counties</option>
                {counties.map((c) => (
                  <option key={c} value={c}>{c}</option>
                ))}
              </select>
            </div>
          )}
        </div>
      )}

      <p className="text-sm text-muted-foreground">
        {shown.length} of {entries.length} shown
      </p>

      {shown.length === 0 ? (
        <div className="card text-center py-12">
          <p className="text-sm text-muted-foreground">
            Nothing matches that. Try clearing the filters.
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5">
          {shown.map((entry) => (
            <div key={entry.id} className="card flex flex-col gap-3">
              {entry.image_url && (
                // eslint-disable-next-line @next/next/no-img-element
                <img
                  src={entry.image_url}
                  alt={entry.title}
                  className="w-full h-40 object-cover rounded-lg"
                />
              )}

              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <h3 className="font-bold text-foreground text-sm leading-snug">{entry.title}</h3>
                  {entry.subtitle && (
                    <p className="text-xs text-muted-foreground mt-0.5">{entry.subtitle}</p>
                  )}
                </div>
                {entry.is_featured && <span className="premium-badge shrink-0">Featured</span>}
              </div>

              <div className="flex flex-wrap items-center gap-2">
                {entry.category && (
                  <span className="bg-secondary text-secondary-foreground px-2 py-0.5 rounded-full text-xs font-medium">
                    {entry.category}
                  </span>
                )}
                {whereLabel(entry) && (
                  <span className="flex items-center gap-1 text-xs text-muted-foreground">
                    <Icon name="MapPinIcon" size={12} />
                    {whereLabel(entry)}
                  </span>
                )}
              </div>

              {showCoverage && entry.coverage && (
                <p className="text-xs text-muted-foreground">
                  <span className="font-semibold text-foreground">Covers:</span> {entry.coverage}
                </p>
              )}

              {entry.description && (
                <p className="text-sm text-muted-foreground leading-relaxed line-clamp-3">
                  {entry.description}
                </p>
              )}

              {Object.keys(entry.details).length > 0 && (
                <dl className="grid grid-cols-2 gap-x-3 gap-y-1">
                  {Object.entries(entry.details).slice(0, 6).map(([k, v]) => (
                    <div key={k} className="min-w-0">
                      <dt className="text-[11px] uppercase tracking-wide text-muted-foreground truncate">{k}</dt>
                      <dd className="text-xs font-semibold text-foreground truncate">{String(v)}</dd>
                    </div>
                  ))}
                </dl>
              )}

              <div className="mt-auto pt-2 flex items-center justify-between gap-3 border-t border-border">
                <p className="font-bold text-primary">{priceLabel(entry)}</p>
                <BoardEnquiryButton
                  entry={entry}
                  label={enquiryLabel}
                  className="btn-primary text-xs py-2 px-3"
                />
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
